import React, { Component } from "react";
import {
  Col,
  Row,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CustomInput,
} from "reactstrap";
import {
  getTransportServicesAdmin,
  updateTransportService,
} from "../../Endpoint/Admin/index";
import BranchAssignment from "../../components/Common/BranchAssignment";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { canWrite } from "../../services/PermissionService";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, MiniStat, StatRow, TabPills, Chip, Btn, IconBtn, MODAL_CSS,
} from "../../components/Console/ConsoleUI";

/**
 * Transport services — what the hotel offers and what each costs.
 *
 * A reskin: the same admin service list and the same update call, the same
 * category tabs, the same editor with its BranchAssignment and per-branch
 * price overrides, and the same canWrite() gate on the edit control.
 *
 * Each service prices itself differently — a fixed fee, a tier list, an hourly
 * rate, a per-km minimum — so the card shows the price in whichever of those
 * shapes the record actually uses rather than flattening all four into one
 * number that would be wrong for three of them.
 */

const CSS = MODAL_CSS;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

class TransportServices extends Component {
  constructor(props) {
    super(props);
    this.state = {
      services: [],
      isLoaded: false,
      showEditModal: false,
      editingService: null,
      activeTab: "protocol",
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Services", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadServices();
  }

  loadServices = () => {
    getTransportServicesAdmin()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ services: result.data, isLoaded: true });
        } else {
          this.setState({ isLoaded: true });
          toast.error(result?.message || "Failed to load services");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading services");
      });
  };

  handleEdit = (service) => {
    // Use dynamic tierPricing array if available, otherwise fallback to legacy
    let tierPricing = service.tierPricing || [];

    // If no tierPricing but has availableTiers, initialize from legacy pricing
    if (tierPricing.length === 0 && service.availableTiers) {
      tierPricing = service.availableTiers.map((tier) => {
        const legacyDayPrice = service.tieredPricing?.[tier.code.toLowerCase()] || 0;
        const legacyNightPrice = service.nightPricing?.[tier.code.toLowerCase()] || 0;
        return {
          tierCode: tier.code,
          tierId: tier._id,
          tierName: tier.name,
          tierColor: tier.color,
          tierIcon: tier.icon,
          displayOrder: tier.displayOrder,
          dayPrice: legacyDayPrice,
          nightPrice: legacyNightPrice,
        };
      });
    } else if (tierPricing.length > 0) {
      // Preserve existing nightPrice values
      tierPricing = tierPricing.map((tier) => ({
        ...tier,
        nightPrice: tier.nightPrice || 0,
      }));
    }

    // Keep legacy fields for backward compatibility
    const tieredPricing = service.tieredPricing || { standard: 0, comfort: 0, vip: 0 };
    const nightPricing = service.nightPricing || {
      standard: 0,
      comfort: 0,
      vip: 0,
    };

    this.setState({
      editingService: {
        ...service,
        tierPricing, // Dynamic tier pricing array
        discount: service.discount || {
          isEnabled: false,
          type: "percentage",
          value: 0,
          displayLabel: "",
        },
        tieredPricing,
        nightPricing,
        hourlyPricing: service.hourlyPricing || {
          ratePerHour: 0,
          minimumHours: 4,
          standardRate: 0,
          comfortRate: 0,
          vipRate: 0,
        },
        outstationRates: service.outstationRates || {
          dayRateMultiplier: 1.0,
          nightRateMultiplier: 1.5,
          nightStartHour: 20,
          nightEndHour: 6,
          driverAllowancePerNight: 50,
        },
        branchIds: (service.branchIds || []).map((b) =>
          b && b._id ? b._id : String(b)
        ),
        excludedBranchIds: (service.excludedBranchIds || []).map((b) =>
          b && b._id ? b._id : String(b)
        ),
        branchPriceOverrides: service.branchPriceOverrides || [],
      },
      showEditModal: true,
    });
  };

  handleSave = () => {
    const { editingService } = this.state;

    const updateData = {
      basePrice: editingService.basePrice,
      description: editingService.description,
      isActive: editingService.isActive,
      estimatedDuration: editingService.estimatedDuration,
      maxPassengers: editingService.maxPassengers,
      maxLuggage: editingService.maxLuggage,
      minimumKm: editingService.minimumKm,
      minimumCharge: editingService.minimumCharge,
      discount: editingService.discount,
      branchIds: editingService.branchIds || [],
      excludedBranchIds: editingService.excludedBranchIds || [],
      branchPriceOverrides: editingService.branchPriceOverrides || [],
    };

    // Include tiered pricing for tiered services (protocol, transport, combo)
    if (editingService.pricingMode === "tiered") {
      // Use dynamic tierPricing array
      updateData.tierPricing = editingService.tierPricing;

      // Also update legacy fields for backward compatibility
      const legacyTiered = {};
      const legacyNight = {};
      (editingService.tierPricing || []).forEach((tp) => {
        const key = tp.tierCode.toLowerCase();
        legacyTiered[key] = tp.dayPrice || 0;
        legacyNight[key] = tp.nightPrice || 0;
      });
      updateData.tieredPricing = { ...editingService.tieredPricing, ...legacyTiered };
      updateData.nightPricing = { ...editingService.nightPricing, ...legacyNight };
    }

    // Include hourly pricing for day rental
    if (editingService.pricingMode === "hourly") {
      updateData.hourlyPricing = editingService.hourlyPricing;
    }

    // Include outstation rates for outstation services
    if (editingService.pricingMode === "day_night_km") {
      updateData.outstationRates = editingService.outstationRates;
    }

    updateTransportService(editingService._id, updateData)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Service updated successfully");
          this.setState({ showEditModal: false, editingService: null });
          this.loadServices();
        } else {
          toast.error(response?.message || "Failed to update service");
        }
      })
      .catch(() => toast.error("Error updating service"));
  };

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  getServiceIcon = (service) => {
    return service.icon || "ri-car-line";
  };

  getServiceColor = (code) => {
    const colors = {
      // New service codes
      PROTOCOL_ARRIVAL: "info",
      PROTOCOL_DEPARTURE: "info",
      TRANSPORT_ARRIVAL: "primary",
      TRANSPORT_DEPARTURE: "primary",
      COMBO_ARRIVAL: "success",
      COMBO_DEPARTURE: "success",
      DAY_RENTAL: "warning",
      OUTSTATION_ONEWAY: "danger",
      OUTSTATION_RETURN: "danger",
      // Legacy codes
      PICKUP: "primary",
      DROP: "info",
      PICK_DROP: "success",
      MEET_GREET: "warning",
      INTERCITY: "success",
      OUTSTATION: "danger",
    };
    return colors[code] || "secondary";
  };

  getPricingModeLabel = (mode) => {
    const labels = {
      fixed: "Fixed Price",
      tiered: "Tiered (Standard/Comfort/VIP)",
      hourly: "Hourly Rate",
      per_km: "Per KM",
      day_night_km: "Day/Night per KM",
    };
    return labels[mode] || mode;
  };

  getCategoryLabel = (category) => {
    const labels = {
      protocol: "Protocol (Meet & Greet)",
      transport: "Transport (Airport)",
      protocol_transport: "Protocol & Transport",
      day_rental: "Day Rental",
      outstation: "Outstation",
      // Legacy
      airport: "Airport Transfer",
      intercity: "Inter-city",
    };
    return labels[category] || category;
  };

  /**
   * Price, in the shape the service's own pricing mode takes: a tier list, an
   * hourly rate card, a per-km minimum or a single fixed figure. A discount
   * shows the original struck through beside it — a rate that quietly changed
   * is a rate nobody can check.
   */
  renderPriceWithDiscount = (service) => {
    const { discount, basePrice, pricingMode, tieredPricing, nightPricing, hourlyPricing, tierPricing } = service;
    const on = discount?.isEnabled && discount?.value > 0;
    const badge = on ? (
      <div style={{ marginTop: 6 }}>
        <Chip tone="good">
          {discount.displayLabel ||
            `${discount.value}${discount.type === "percentage" ? "%" : "$"} OFF`}
        </Chip>
      </div>
    ) : null;

    const line = (label, value, night) => (
      <div key={label} style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 13 }}>
        <span style={{ color: C.taupeLight, minWidth: 70 }}>{label}</span>
        <span style={{ fontWeight: 500, color: C.ink }}>{value}</span>
        {night ? (
          <span style={{ color: C.taupeLight, display: "inline-flex", alignItems: "center", gap: 3 }}>
            / {night}
            <span className="ms" style={{ fontSize: 14 }}>dark_mode</span>
          </span>
        ) : null}
      </div>
    );

    // Tiered pricing (Protocol, Transport, Combo) — dynamic tiers first.
    if (pricingMode === "tiered") {
      if (tierPricing && tierPricing.length > 0) {
        return (
          <div>
            {tierPricing.slice(0, 3).map((t) =>
              line(
                t.tierName || t.tierCode,
                money(t.dayPrice || 0),
                t.nightPrice > 0 ? money(t.nightPrice) : null
              )
            )}
            {tierPricing.length > 3 && (
              <div style={{ fontSize: 13, color: C.taupeLight }}>
                +{tierPricing.length - 3} more tiers
              </div>
            )}
            {badge}
          </div>
        );
      }

      if (tieredPricing) {
        return (
          <div>
            {line("Standard", money(tieredPricing.standard || 0), nightPricing?.standard > 0 ? money(nightPricing.standard) : null)}
            {line("Comfort", money(tieredPricing.comfort || 0), nightPricing?.comfort > 0 ? money(nightPricing.comfort) : null)}
            {line("VIP", money(tieredPricing.vip || 0), nightPricing?.vip > 0 ? money(nightPricing.vip) : null)}
            {badge}
          </div>
        );
      }
    }

    // Hourly pricing (Day Rental)
    if (pricingMode === "hourly" && hourlyPricing) {
      return (
        <div>
          {line("Standard", `${money(hourlyPricing.standardRate || 0)}/hr`)}
          {line("Comfort", `${money(hourlyPricing.comfortRate || 0)}/hr`)}
          {line("VIP", `${money(hourlyPricing.vipRate || 0)}/hr`)}
          <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>
            Minimum {hourlyPricing.minimumHours || 4} hrs
          </div>
          {badge}
        </div>
      );
    }

    // Per-km pricing (Outstation)
    if (pricingMode === "per_km" || pricingMode === "day_night_km") {
      const minCharge = service.minimumCharge || 0;
      const cut = !on
        ? null
        : discount.type === "percentage"
          ? minCharge * (1 - discount.value / 100)
          : minCharge - discount.value;
      return (
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
            <span style={{ fontSize: 13, color: C.taupeLight }}>Min charge</span>
            {on ? (
              <React.Fragment>
                <span style={{ fontSize: 13, color: C.taupeLight, textDecoration: "line-through" }}>
                  {money(minCharge)}
                </span>
                <span style={{ fontSize: 17, fontWeight: 500, color: "#2f7d33" }}>{money(cut)}</span>
              </React.Fragment>
            ) : (
              <span style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>{money(minCharge)}</span>
            )}
          </div>
          {badge}
        </div>
      );
    }

    // Fixed price services
    if (on) {
      const cut =
        discount.type === "percentage"
          ? basePrice * (1 - discount.value / 100)
          : basePrice - discount.value;
      return (
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 14, color: C.taupeLight, textDecoration: "line-through" }}>
              {money(basePrice)}
            </span>
            <span style={{ fontSize: 20, fontWeight: 500, color: "#2f7d33" }}>{money(cut)}</span>
          </div>
          {badge}
        </div>
      );
    }

    return <span style={{ fontSize: 20, fontWeight: 500, color: C.ink }}>{money(basePrice)}</span>;
  };

  updateEditingService = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        [field]: value,
      },
    }));
  };

  updateDiscount = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        discount: {
          ...prevState.editingService.discount,
          [field]: value,
        },
      },
    }));
  };

  updateOutstationRates = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        outstationRates: {
          ...prevState.editingService.outstationRates,
          [field]: value,
        },
      },
    }));
  };

  updateTieredPricing = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        tieredPricing: {
          ...prevState.editingService.tieredPricing,
          [field]: value,
        },
      },
    }));
  };

  // Update dynamic tier pricing (for linked service tiers)
  updateDynamicTierPricing = (tierCode, field, value) => {
    this.setState((prevState) => {
      const tierPricing = [...(prevState.editingService.tierPricing || [])];
      const tierIndex = tierPricing.findIndex((tp) => tp.tierCode === tierCode);

      if (tierIndex >= 0) {
        tierPricing[tierIndex] = {
          ...tierPricing[tierIndex],
          [field]: value,
        };

      }

      return {
        editingService: {
          ...prevState.editingService,
          tierPricing,
        },
      };
    });
  };

  updateHourlyPricing = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        hourlyPricing: {
          ...prevState.editingService.hourlyPricing,
          [field]: value,
        },
      },
    }));
  };

  /** One service, as a card: what it is, what it costs, what it includes. */
  renderServiceCard = (service) => {
    const facts =
      service.pricingMode === "fixed"
        ? [
            { label: "Minutes", value: service.estimatedDuration || "—" },
            { label: "Guests", value: service.maxPassengers || "—" },
            { label: "Luggage", value: service.maxLuggage || "—" },
          ]
        : [
            { label: "Minutes", value: service.estimatedDuration || "—" },
            { label: "Guests", value: service.maxPassengers || "—" },
            { label: "Min km", value: service.minimumKm || 0 },
          ];

    return (
      <div
        key={service._id}
        style={{
          background: "#fff",
          border: `1px solid ${C.tint}`,
          borderRadius: 18,
          padding: 22,
          opacity: service.isActive ? 1 : 0.72,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <span
            style={{
              width: 48, height: 48, borderRadius: 14, flex: "0 0 48px",
              background: C.iconTint, color: C.brown,
              display: "grid", placeItems: "center",
            }}
          >
            <i className={this.getServiceIcon(service)} style={{ fontSize: 22 }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>{service.serviceName}</div>
            <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
              <Chip tone={service.isActive ? "good" : "neutral"}>
                {service.isActive ? "Active" : "Off"}
              </Chip>
              <Chip>{this.getPricingModeLabel(service.pricingMode)}</Chip>
            </div>
          </div>
          {canWrite() && (
            <IconBtn icon="edit" title="Edit service" onClick={() => this.handleEdit(service)} />
          )}
        </div>

        {service.description && (
          <div style={{ fontSize: 14, color: C.taupe, lineHeight: 1.5 }}>{service.description}</div>
        )}

        {/* Caption above figure throughout, so a two-line tier list and a
            single number still line up along the same top edge. */}
        <div
          style={{
            display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 12,
            paddingTop: 16, borderTop: `1px solid ${C.tint}`,
          }}
        >
          <div>
            <div style={{ marginBottom: 6, fontSize: 12, color: C.taupeLight }}>
              {service.pricingMode === "fixed" ? "Base price" : "Rate"}
            </div>
            {this.renderPriceWithDiscount(service)}
          </div>
          {facts.map((f) => (
            <div key={f.label}>
              <div style={{ marginBottom: 6, fontSize: 12, color: C.taupeLight }}>{f.label}</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: C.ink }}>{f.value}</div>
            </div>
          ))}
        </div>

        {service.freeWithServices && service.freeWithServices.length > 0 && (
          <div
            style={{
              padding: "11px 14px", borderRadius: 12, background: "rgba(76,175,80,.10)",
              fontSize: 13, color: "#2f7d33",
            }}
          >
            Free when bundled with {service.freeWithServices.join(", ")}
          </div>
        )}

        {service.pricingMode === "day_night_km" && service.outstationRates && (
          <div
            style={{
              padding: "11px 14px", borderRadius: 12, background: C.surface,
              fontSize: 13, color: C.taupe,
            }}
          >
            Night rate ×{service.outstationRates.nightRateMultiplier} · driver allowance{" "}
            {money(service.outstationRates.driverAllowancePerNight)}/night
          </div>
        )}
      </div>
    );
  };

  render() {
    const { services, showEditModal, editingService, activeTab, isLoaded } = this.state;

    // Group services by category. Legacy buckets only appear once they hold
    // something, so a clean install is not offered two dead tabs.
    const by = (c) => services.filter((s) => s.category === c);
    const groups = {
      protocol: by("protocol"),
      transport: by("transport"),
      combo: by("protocol_transport"),
      day_rental: by("day_rental"),
      outstation: by("outstation"),
      airport: by("airport"),
      intercity: by("intercity"),
    };

    const tabs = [
      { value: "protocol", label: `Protocol · ${groups.protocol.length}` },
      { value: "transport", label: `Transport · ${groups.transport.length}` },
      { value: "combo", label: `Protocol & transport · ${groups.combo.length}` },
      { value: "day_rental", label: `Day rental · ${groups.day_rental.length}` },
      { value: "outstation", label: `Outstation · ${groups.outstation.length}` },
      ...(groups.airport.length ? [{ value: "airport", label: `Legacy airport · ${groups.airport.length}` }] : []),
      ...(groups.intercity.length ? [{ value: "intercity", label: `Legacy inter-city · ${groups.intercity.length}` }] : []),
    ];

    const shown = groups[activeTab] || [];
    const active = services.filter((s) => s.isActive).length;
    const discounted = services.filter((s) => s.discount?.isEnabled && s.discount?.value > 0).length;
    const modes = new Set(services.map((s) => s.pricingMode).filter(Boolean)).size;

    return (
      <ConsolePage
        title="Services"
        subtitle={`Transport · ${services.length} service${services.length === 1 ? "" : "s"}${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <Btn
              kind="quiet"
              icon="directions_car"
              onClick={() => this.props.history.push("/transport/vehicle-types")}
            >
              Vehicle types
            </Btn>
            <Btn icon="arrow_back" onClick={() => this.props.history.push("/transport/requests")}>
              Requests
            </Btn>
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <MiniStat
              label="Services bookable"
              value={`${active} / ${services.length}`}
              hint={`${services.length - active} switched off`}
            />
            <MiniStat label="Categories in use" value={tabs.length} hint="tabs below" />
            <MiniStat label="Pricing models" value={modes} hint="fixed, tiered, hourly, per-km" />
            <MiniStat label="Running a discount" value={discounted} hint="price cut in the app" />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <TabPills tabs={tabs} value={activeTab} onChange={this.toggleTab} />
            <div style={{ padding: 22 }}>
              {!isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading services…
                </div>
              ) : shown.length === 0 ? (
                <div style={{ padding: "44px 0", textAlign: "center", color: C.taupeLight }}>
                  <div style={{ fontSize: 15, color: C.taupe }}>
                    Nothing configured in this category yet.
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Seed the defaults with{" "}
                    <code style={{ color: C.brown }}>node database/seedTransportServices.js</code>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
                    gap: 18,
                  }}
                >
                  {shown.map((service) => this.renderServiceCard(service))}
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* Edit Modal */}
        <Modal
          isOpen={showEditModal}
          toggle={() => this.setState({ showEditModal: false })}
          size="lg"
          className="con-modal"
        >
          <ModalHeader toggle={() => this.setState({ showEditModal: false })}>
            Edit service: {editingService?.serviceName}
          </ModalHeader>
          <ModalBody>
            {editingService && (
              <Row>
                {/* Basic Info */}
                <Col md={6}>
                  <FormGroup>
                    <Label>Base Price / Min Charge ($)</Label>
                    <Input
                      type="number"
                      value={editingService.pricingMode === "fixed" ? editingService.basePrice : editingService.minimumCharge}
                      onChange={(e) =>
                        editingService.pricingMode === "fixed"
                          ? this.updateEditingService("basePrice", parseFloat(e.target.value) || 0)
                          : this.updateEditingService("minimumCharge", parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Estimated Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={editingService.estimatedDuration}
                      onChange={(e) => this.updateEditingService("estimatedDuration", parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>

                {editingService.pricingMode !== "fixed" && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Minimum KM</Label>
                      <Input
                        type="number"
                        value={editingService.minimumKm}
                        onChange={(e) => this.updateEditingService("minimumKm", parseInt(e.target.value) || 0)}
                      />
                    </FormGroup>
                  </Col>
                )}

                <Col md={editingService.pricingMode !== "fixed" ? 6 : 6}>
                  <FormGroup>
                    <Label>Max Passengers</Label>
                    <Input
                      type="number"
                      value={editingService.maxPassengers}
                      onChange={(e) => this.updateEditingService("maxPassengers", parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Max Luggage</Label>
                    <Input
                      type="number"
                      value={editingService.maxLuggage}
                      onChange={(e) => this.updateEditingService("maxLuggage", parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12}>
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      rows={2}
                      value={editingService.description}
                      onChange={(e) => this.updateEditingService("description", e.target.value)}
                    />
                  </FormGroup>
                </Col>

                {/* Dynamic Tiered Pricing (Protocol, Transport, Combo) */}
                {editingService.pricingMode === "tiered" && (
                  <>
                    <Col xs={12}>
                      <hr />
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          <i className="ri-price-tag-3-line mr-2"></i>
                          Tier Pricing
                        </h6>
                        <Link to="/transport/service-tiers" className="btn btn-sm btn-outline-primary">
                          <i className="ri-settings-3-line mr-1"></i>
                          Manage Tiers
                        </Link>
                      </div>
                      <small className="text-muted d-block mb-3">
                        Set day and night prices for each tier.
                      </small>
                    </Col>

                    {/* Dynamic tier pricing - renders all linked service tiers */}
                    {(editingService.tierPricing || []).map((tier) => (
                      <Col md={6} lg={4} key={tier.tierCode}>
                        <div
                          className="p-3 border rounded mb-3"
                          style={{ borderLeft: `4px solid ${tier.tierColor || '#5664d2'}` }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            <i className={`${tier.tierIcon || 'ri-car-line'} mr-2`} style={{ color: tier.tierColor }}></i>
                            <strong>{tier.tierName || tier.tierCode}</strong>
                          </div>
                          <FormGroup className="mb-2">
                            <Label className="small mb-1">
                              <i className="ri-sun-line mr-1 text-warning"></i>
                              Day Price ($)
                            </Label>
                            <Input
                              type="number"
                              bsSize="sm"
                              value={tier.dayPrice || 0}
                              onChange={(e) => this.updateDynamicTierPricing(tier.tierCode, "dayPrice", parseFloat(e.target.value) || 0)}
                            />
                          </FormGroup>
                          <FormGroup className="mb-0">
                            <Label className="small mb-1">
                              <i className="ri-moon-line mr-1 text-info"></i>
                              Night Price ($)
                            </Label>
                            <Input
                              type="number"
                              bsSize="sm"
                              value={tier.nightPrice || 0}
                              onChange={(e) => this.updateDynamicTierPricing(tier.tierCode, "nightPrice", parseFloat(e.target.value) || 0)}
                            />
                          </FormGroup>
                        </div>
                      </Col>
                    ))}

                    {(!editingService.tierPricing || editingService.tierPricing.length === 0) && (
                      <Col xs={12}>
                        <div className="alert alert-warning">
                          <i className="ri-information-line mr-2"></i>
                          No service tiers found. <Link to="/transport/service-tiers">Add service tiers</Link> first.
                        </div>
                      </Col>
                    )}

                    <Col xs={12}>
                      <small className="text-info">
                        <i className="ri-information-line mr-1"></i>
                        Tiers are managed in <Link to="/transport/service-tiers">Service Tiers</Link>. Add new tiers there and they will appear here automatically.
                      </small>
                    </Col>
                  </>
                )}

                {/* Hourly Pricing (Day Rental) */}
                {editingService.pricingMode === "hourly" && (
                  <>
                    <Col xs={12}>
                      <hr />
                      <h6>
                        <i className="ri-timer-line mr-2"></i>
                        Hourly Pricing
                      </h6>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Minimum Hours</Label>
                        <Input
                          type="number"
                          min="1"
                          value={editingService.hourlyPricing?.minimumHours || 4}
                          onChange={(e) => this.updateHourlyPricing("minimumHours", parseInt(e.target.value) || 4)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Default Rate Per Hour ($)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.ratePerHour || 0}
                          onChange={(e) => this.updateHourlyPricing("ratePerHour", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Standard Rate ($/hr)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.standardRate || 0}
                          onChange={(e) => this.updateHourlyPricing("standardRate", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Comfort Rate ($/hr)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.comfortRate || 0}
                          onChange={(e) => this.updateHourlyPricing("comfortRate", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>VIP Rate ($/hr)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.vipRate || 0}
                          onChange={(e) => this.updateHourlyPricing("vipRate", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                {/* Outstation Rates (only for day_night_km services) */}
                {editingService.pricingMode === "day_night_km" && (
                  <>
                    <Col xs={12}>
                      <hr />
                      <h6>
                        <i className="ri-moon-line mr-2"></i>
                        Outstation Rates
                      </h6>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Day Rate Multiplier</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={editingService.outstationRates?.dayRateMultiplier || 1.0}
                          onChange={(e) => this.updateOutstationRates("dayRateMultiplier", parseFloat(e.target.value) || 1.0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Night Rate Multiplier</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={editingService.outstationRates?.nightRateMultiplier || 1.5}
                          onChange={(e) => this.updateOutstationRates("nightRateMultiplier", parseFloat(e.target.value) || 1.5)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Night Start Hour</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={editingService.outstationRates?.nightStartHour || 20}
                          onChange={(e) => this.updateOutstationRates("nightStartHour", parseInt(e.target.value) || 20)}
                        />
                        <small className="text-muted">0-23 (e.g., 20 = 8 PM)</small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Night End Hour</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={editingService.outstationRates?.nightEndHour || 6}
                          onChange={(e) => this.updateOutstationRates("nightEndHour", parseInt(e.target.value) || 6)}
                        />
                        <small className="text-muted">0-23 (e.g., 6 = 6 AM)</small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Driver Allowance Per Night ($)</Label>
                        <Input
                          type="number"
                          value={editingService.outstationRates?.driverAllowancePerNight || 50}
                          onChange={(e) => this.updateOutstationRates("driverAllowancePerNight", parseFloat(e.target.value) || 50)}
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                {/* Discount Settings */}
                <Col xs={12}>
                  <hr />
                  <h6>
                    <i className="ri-percent-line mr-2"></i>
                    Discount Settings
                  </h6>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Enable Discount</Label>
                    <div>
                      <CustomInput
                        type="switch"
                        id="discountEnabled"
                        checked={editingService.discount?.isEnabled || false}
                        onChange={(e) => this.updateDiscount("isEnabled", e.target.checked)}
                      />
                    </div>
                  </FormGroup>
                </Col>
                {editingService.discount?.isEnabled && (
                  <>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Type</Label>
                        <Input
                          type="select"
                          value={editingService.discount?.type || "percentage"}
                          onChange={(e) => this.updateDiscount("type", e.target.value)}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount ($)</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Value</Label>
                        <Input
                          type="number"
                          value={editingService.discount?.value || 0}
                          onChange={(e) => this.updateDiscount("value", parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Display Label</Label>
                        <Input
                          type="text"
                          value={editingService.discount?.displayLabel || ""}
                          onChange={(e) => this.updateDiscount("displayLabel", e.target.value)}
                          placeholder="e.g., 10% OFF"
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                <Col xs={12} className="mt-3">
                  <BranchAssignment
                    branchIds={editingService.branchIds}
                    excludedBranchIds={editingService.excludedBranchIds}
                    overrides={editingService.branchPriceOverrides}
                    priceFields={[{ key: "basePrice", label: "Base Price" }]}
                    onChange={({ branchIds, excludedBranchIds, overrides }) =>
                      this.setState((prev) => ({
                        editingService: {
                          ...prev.editingService,
                          branchIds,
                          excludedBranchIds,
                          branchPriceOverrides: overrides,
                        },
                      }))
                    }
                  />
                </Col>

                <Col xs={12} className="mt-3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editingService.isActive}
                        onChange={(e) => this.updateEditingService("isActive", e.target.checked)}
                      />
                      {" "}Service is Active
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showEditModal: false })}>
              Cancel
            </Btn>
            <Btn onClick={this.handleSave}>
              Save changes
            </Btn>
          </ModalFooter>
        </Modal>
      </ConsolePage>
    );
  }
}

export default TransportServices;
